import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = ({ onLogin }) => {
  const [isRegister, setIsRegister] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const formatError = (detail) => {
    if (detail == null) return 'Something went wrong. Please try again.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
    if (detail && typeof detail.msg === 'string') return detail.msg;
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isRegister) {
        const { data } = await axios.post(
          `${API}/auth/register`,
          { username, password, display_name: displayName || undefined },
          { withCredentials: true }
        );
        toast.success(`Welcome, ${data.display_name}!`);
        onLogin(data);
      } else {
        const { data } = await axios.post(
          `${API}/auth/login`,
          { username, password },
          { withCredentials: true }
        );
        toast.success(`Welcome back, ${data.display_name}!`);
        onLogin(data);
      }
    } catch (err) {
      const msg = formatError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <img 
            src="https://customer-assets.emergentagent.com/job_presenter-toolbox/artifacts/xguxj4wx_KL1%20Logo-01.png" 
            alt="KL1 Radio Logo" 
            className="login-logo"
          />
          <p>Presenter Portal</p>
        </div>
        
        <div className="auth-tabs" data-testid="auth-tabs">
          <button 
            className={`auth-tab ${!isRegister ? 'active' : ''}`} 
            onClick={() => { setIsRegister(false); setError(''); }}
            data-testid="login-tab"
          >
            Sign In
          </button>
          <button 
            className={`auth-tab ${isRegister ? 'active' : ''}`} 
            onClick={() => { setIsRegister(true); setError(''); }}
            data-testid="register-tab"
          >
            Register
          </button>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <Input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="password-input"
              data-testid="username-input"
              required
              autoComplete="username"
            />
          </div>
          
          {isRegister && (
            <div className="form-group">
              <Input
                type="text"
                placeholder="Display Name (optional)"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="password-input"
                data-testid="display-name-input"
                autoComplete="off"
              />
            </div>
          )}

          <div className="form-group">
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="password-input"
              data-testid="password-input"
              required
              autoComplete={isRegister ? 'new-password' : 'current-password'}
            />
          </div>

          {error && (
            <div className="auth-error" data-testid="auth-error">
              {error}
            </div>
          )}
          
          <Button 
            type="submit" 
            className="login-button"
            disabled={loading}
            data-testid="login-button"
          >
            {loading ? 'Please wait...' : (isRegister ? 'Create Account' : 'Sign In')}
          </Button>
        </form>
        
        <div className="login-footer">
          <p>West Norfolk's Local Radio</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
